/* PaymentSuccess — polls the backend after Stripe redirects the user here
 * with ?session_id={CHECKOUT_SESSION_ID}. The backend verifies the session
 * via Stripe and (once) credits the user's subscription. We poll up to 8
 * times at 2-second intervals — the typical happy path resolves on the first
 * call, but Stripe webhooks can take a few seconds to settle.
 */
import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Check, X as XIcon, ArrowClockwise } from "@phosphor-icons/react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

const MAX_POLLS = 8;
const POLL_INTERVAL_MS = 2000;
const PACKAGE_LABEL = {
  "1mo": "1 month", "3mo": "3 months", "6mo": "6 months", "annual": "12 months",
};

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const nav = useNavigate();
  const { refresh } = useAuth();
  const [state, setState] = useState({ phase: "checking", attempts: 0, status: null });
  const stopRef = useRef(false);

  useEffect(() => {
    if (!sessionId) {
      setState({ phase: "error", attempts: 0, status: null, error: "Missing session id" });
      return;
    }
    let attempts = 0;
    const tick = async () => {
      if (stopRef.current) return;
      attempts += 1;
      try {
        const { data } = await api.get(`/checkout/status/${sessionId}`);
        setState({ phase: "checking", attempts, status: data });
        if (data.payment_status === "paid") {
          stopRef.current = true;
          setState({ phase: "success", attempts, status: data });
          await refresh();
          return;
        }
        if (data.status === "expired" || data.payment_status === "unpaid_expired") {
          stopRef.current = true;
          setState({ phase: "expired", attempts, status: data });
          return;
        }
        if (attempts >= MAX_POLLS) {
          stopRef.current = true;
          setState({ phase: "timeout", attempts, status: data });
          return;
        }
        setTimeout(tick, POLL_INTERVAL_MS);
      } catch (e) {
        stopRef.current = true;
        setState({ phase: "error", attempts, status: null, error: e?.response?.data?.detail || "Could not verify payment" });
      }
    };
    tick();
    return () => { stopRef.current = true; };
  }, [sessionId, refresh]);

  return (
    <div className="min-h-[70vh] max-w-2xl mx-auto px-6 py-20 text-center">
      {state.phase === "checking" && (
        <div data-testid="pay-checking">
          <ArrowClockwise size={56} weight="bold" className="text-rf-orange mx-auto mb-6 animate-spin" />
          <h1 className="font-display text-3xl font-black text-rf-ink mb-3">Verifying your payment…</h1>
          <p className="text-rf-ink-soft">Hang on — checking with Stripe (attempt {state.attempts || 1} of {MAX_POLLS}).</p>
        </div>
      )}
      {state.phase === "success" && (
        <div data-testid="pay-success">
          <div className="w-20 h-20 rounded-full bg-emerald-500/20 text-emerald-600 flex items-center justify-center mx-auto mb-6"><Check size={44} weight="bold" /></div>
          {state.status?.kind === "gift" ? (
            <>
              <h1 className="font-display text-4xl font-black text-rf-ink mb-3">Gift on its way ★</h1>
              <p className="text-rf-ink-soft text-lg mb-4">
                The lucky recipient just received their 12-month Recipe Filer gift code by email.
              </p>
              {state.status?.issued_code && (
                <div className="inline-block px-6 py-4 rounded-2xl bg-rf-amber/15 border border-rf-amber/40 mb-6">
                  <p className="text-xs font-black uppercase tracking-wider text-rf-orange mb-1">Gift code</p>
                  <p className="font-mono font-black text-2xl tracking-widest text-rf-ink" data-testid="pay-gift-code">{state.status.issued_code}</p>
                  <p className="text-[11px] text-rf-ink-soft mt-2">Keep this safe in case the email gets lost.</p>
                </div>
              )}
              <div className="flex gap-3 justify-center">
                <button onClick={() => nav("/dashboard")} className="btn-amber">Back to dashboard</button>
                <Link to="/pricing" className="btn-outline-ink">Send another</Link>
              </div>
            </>
          ) : (
            <>
              <h1 className="font-display text-4xl font-black text-rf-ink mb-3">You&apos;re in.</h1>
              <p className="text-rf-ink-soft text-lg mb-8">
                Your <b>{PACKAGE_LABEL[state.status?.package_id] || "subscription"}</b> is now active.
                Unlimited recipes, unlimited cookbooks and access to order real bound books via Lulu are unlocked.
              </p>
              <div className="flex gap-3 justify-center">
                <button onClick={() => nav("/dashboard")} className="btn-amber">Go to dashboard</button>
                <Link to="/order-hardback" className="btn-outline-ink">Print options</Link>
              </div>
            </>
          )}
        </div>
      )}
      {state.phase === "expired" && (
        <div data-testid="pay-expired">
          <XIcon size={56} weight="bold" className="text-amber-600 mx-auto mb-6" />
          <h1 className="font-display text-3xl font-black text-rf-ink mb-3">Session expired</h1>
          <p className="text-rf-ink-soft mb-6">Your checkout session timed out without completing. No money has changed hands.</p>
          <Link to="/pricing" className="btn-amber">Try again</Link>
        </div>
      )}
      {state.phase === "timeout" && (
        <div data-testid="pay-timeout">
          <ArrowClockwise size={56} weight="bold" className="text-amber-600 mx-auto mb-6" />
          <h1 className="font-display text-3xl font-black text-rf-ink mb-3">Still processing…</h1>
          <p className="text-rf-ink-soft mb-6">Stripe is taking a little longer than usual to confirm. Your subscription will activate automatically the moment the payment settles — feel free to head back to the app and check again in a minute.</p>
          <Link to="/dashboard" className="btn-amber">Continue</Link>
        </div>
      )}
      {state.phase === "error" && (
        <div data-testid="pay-error">
          <XIcon size={56} weight="bold" className="text-red-600 mx-auto mb-6" />
          <h1 className="font-display text-3xl font-black text-rf-ink mb-3">We couldn&apos;t verify the payment</h1>
          <p className="text-rf-ink-soft mb-6">{state.error}</p>
          <div className="flex gap-3 justify-center">
            <Link to="/pricing" className="btn-amber">Back to pricing</Link>
            <Link to="/dashboard" className="btn-outline-ink">Dashboard</Link>
          </div>
        </div>
      )}
    </div>
  );
}
